// ブラウザから IMF PortWatch の公開 ArcGIS FeatureServer を直接叩くクライアント。
// 認証不要。エージェントがアドホックに港/チョークポイントを検索・取得するのに使う。

const ORG =
  "https://services9.arcgis.com/weJ1QsnbMYJlCHdG/arcgis/rest/services";

const STATIC_SERVICE = {
  port: "PortWatch_ports_database",
  chokepoint: "PortWatch_chokepoints_database",
};
const DAILY_SERVICE = {
  port: "Daily_Ports_Data",
  chokepoint: "Daily_Chokepoints_Data",
};
// 港と要衝で指標列が異なる（docs/portwatch-api.md §6/§7）。
const METRIC_FIELDS = {
  port: "date,portname,portcalls,import,export",
  chokepoint: "date,portname,n_total,capacity",
};
// Spillover Simulator（波及リスク）系。from_portid をキーに港の混乱の波及を返す。
const SPILL_SERVICE = {
  trade: "spillovers_trade", // 国別の貿易リスク額（USD/日）
  port: "spillovers_port_level_impact", // 影響先の港の輸送能力リスク（トン/日）
};

const MAX_RECORDS = 1000; // = maxRecordCount

function escapeSql(value) {
  return String(value ?? "").replace(/'/g, "''");
}

async function arcgisQuery(service, params, { signal, timeoutMs = 30000 } = {}) {
  const query = new URLSearchParams({
    f: "json",
    returnGeometry: "false",
    ...params,
  });
  const url = `${ORG}/${service}/FeatureServer/0/query?${query}`;

  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const composite = signal
    ? AbortSignal.any([signal, timeoutSignal])
    : timeoutSignal;

  let res;
  try {
    res = await fetch(url, { signal: composite });
  } catch (error) {
    if (signal?.aborted) {
      const abortError = new Error("PortWatch API呼び出しを中断しました。", {
        cause: error,
      });
      abortError.name = "AbortError";
      throw abortError;
    }
    if (timeoutSignal.aborted) {
      throw new Error("PortWatch API呼び出しがタイムアウトしました。", {
        cause: error,
      });
    }
    throw new Error(
      `PortWatch APIへの接続に失敗しました: ${
        error instanceof Error ? error.message : String(error)
      }`,
      { cause: error },
    );
  }

  if (!res.ok) throw new Error(`PortWatch API HTTP ${res.status}（${service}）。`);
  const json = await res.json();
  if (json.error) {
    throw new Error(
      `ArcGIS エラー（${service}）: ${json.error.message ?? JSON.stringify(json.error)}`,
    );
  }
  return json;
}

/** 港・チョークポイントを名称/国で検索し portid を特定する。 */
export async function searchLocations(
  { query = "", scope = "any", limit = 20 } = {},
  context = {},
) {
  const scopes = scope === "any" ? ["chokepoint", "port"] : [scope];
  const q = escapeSql(query.trim());
  const locations = [];

  for (const sc of scopes) {
    const service = STATIC_SERVICE[sc];
    if (!service) throw new Error(`scope は port / chokepoint / any: ${scope}`);

    const where = q
      ? `portname LIKE '%${q}%' OR country LIKE '%${q}%' OR fullname LIKE '%${q}%'`
      : "1=1";

    const json = await arcgisQuery(
      service,
      {
        where,
        outFields: "portid,portname,fullname,country,ISO3,lat,lon,vessel_count_total",
        orderByFields: "vessel_count_total DESC",
        resultRecordCount: String(limit),
      },
      context,
    );

    for (const f of json.features ?? []) {
      locations.push({ ...f.attributes, scope: sc });
    }
  }

  return { count: locations.length, locations };
}

/** 指定地点の日次時系列を直近 days 件取得する（昇順）。 */
export async function fetchMetrics(
  { portid, scope = "port", days = 365 } = {},
  context = {},
) {
  const service = DAILY_SERVICE[scope];
  if (!service) throw new Error(`scope は port か chokepoint: ${scope}`);
  if (!portid) throw new Error("portid を指定してください。");

  const json = await arcgisQuery(
    service,
    {
      where: `portid='${escapeSql(portid)}'`,
      outFields: METRIC_FIELDS[scope],
      orderByFields: "date DESC",
      resultRecordCount: String(Math.min(days, MAX_RECORDS)),
    },
    context,
  );

  const rows = (json.features ?? []).map((f) => f.attributes);
  rows.reverse(); // 古い → 新しい

  const portname = rows[0]?.portname ?? portid;
  // portname は全行で重複するので records からは外し、タイトルにだけ使う。
  const records = rows.map((row) => {
    const rest = { ...row };
    delete rest.portname;
    return rest;
  });

  return { portid, scope, portname, records };
}

/**
 * 指定した港が混乱した場合の波及リスク（Spillover Simulator）を上位 limit 件取得する。
 * kind=trade: 国別の貿易リスク額（USD/日, 業種別）。kind=port: 影響先の港の輸送能力リスク（トン/日）。
 * from_portid をキーにするため港のみ対象（チョークポイントは基本的に空を返す）。
 */
export async function fetchSpillovers(
  { portid, kind = "trade", industry = "Total", by = "export", limit = 10 } = {},
  context = {},
) {
  if (!portid) throw new Error("portid を指定してください。");
  const service = SPILL_SERVICE[kind];
  if (!service) throw new Error(`kind は trade か port: ${kind}`);
  const id = escapeSql(portid);
  const cap = String(Math.min(Math.max(limit, 1), 50));

  if (kind === "trade") {
    const sortField =
      by === "import"
        ? "daily_import_value_at_risk"
        : "daily_export_value_at_risk";
    const json = await arcgisQuery(
      service,
      {
        where: `from_portid='${id}' AND industry='${escapeSql(industry)}'`,
        outFields:
          "to_country,to_iso3,daily_export_value_at_risk,daily_import_value_at_risk",
        orderByFields: `${sortField} DESC`,
        resultRecordCount: cap,
      },
      context,
    );
    const rows = (json.features ?? []).map((f) => ({
      country: f.attributes.to_country,
      iso3: f.attributes.to_iso3,
      exportAtRisk: f.attributes.daily_export_value_at_risk,
      importAtRisk: f.attributes.daily_import_value_at_risk,
    }));
    return { kind, portid, industry, by, unit: "US Dollars/day", rows };
  }

  // kind === "port"
  const json = await arcgisQuery(
    service,
    {
      where: `from_portid='${id}'`,
      outFields:
        "to_portid,to_portname,to_country,daily_capacity_at_risk,relative_capacity_at_risk,average_transit_days",
      orderByFields: "daily_capacity_at_risk DESC",
      resultRecordCount: cap,
    },
    context,
  );
  const rows = (json.features ?? []).map((f) => ({
    toPortid: f.attributes.to_portid,
    toPortname: f.attributes.to_portname,
    country: f.attributes.to_country,
    capacityAtRisk: f.attributes.daily_capacity_at_risk,
    relativeAtRiskPct: f.attributes.relative_capacity_at_risk,
    transitDays: f.attributes.average_transit_days,
  }));
  return { kind, portid, unit: "metric tons/day", rows };
}

/**
 * 地図描画用: 指定港の波及ネットワーク（from→to の座標つき）を取得する。
 * spillovers_port_level_impact は from/to の港座標を持つので、そのまま弧を引ける（港のみ対象）。
 */
export async function fetchSpilloverNetwork(
  { portid, limit = 25 } = {},
  context = {},
) {
  if (!portid) throw new Error("portid を指定してください。");
  const json = await arcgisQuery(
    "spillovers_port_level_impact",
    {
      where: `from_portid='${escapeSql(portid)}'`,
      outFields:
        "from_lat,from_lon,to_portid,to_portname,to_country,to_lat,to_lon,daily_capacity_at_risk,relative_capacity_at_risk,average_transit_days",
      orderByFields: "daily_capacity_at_risk DESC",
      resultRecordCount: String(Math.min(Math.max(limit, 1), 100)),
    },
    context,
  );

  const feats = json.features ?? [];
  const f0 = feats[0]?.attributes;
  const from =
    f0 && f0.from_lon != null && f0.from_lat != null
      ? [f0.from_lon, f0.from_lat]
      : null;

  const arcs = feats
    .filter((f) => f.attributes.to_lon != null && f.attributes.to_lat != null)
    .map((f) => {
      const a = f.attributes;
      return {
        to: [a.to_lon, a.to_lat],
        toPortid: a.to_portid,
        toPortname: a.to_portname,
        country: a.to_country,
        capacityAtRisk: a.daily_capacity_at_risk,
        relativeAtRiskPct: a.relative_capacity_at_risk,
        transitDays: a.average_transit_days,
      };
    });

  return { portid, from, arcs };
}
